import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { userApi, billingApi } from '../api/client'
import type { BillingSummary, Invoice, UsageQuota, QuotaItem } from '../types'
import { LEVEL_META, LEVEL_QUOTA, LEVEL_ORDER } from '../constants/plan'
import type { UserLevel } from '../constants/plan'

// ── helpers ──────────────────────────────────────────────

function formatFee(fee: number) {
  return fee === 0 ? '무료' : `${fee.toLocaleString('ko-KR')}원/월`
}
function formatKRW(amount: number) {
  return amount === 0 ? '없음' : `${amount.toLocaleString('ko-KR')}원`
}
function calcProration(newFee: number, oldFee: number) {
  const today = new Date()
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
  const remainingDays = daysInMonth - today.getDate() + 1
  const newPortion = Math.round((newFee / 30) * remainingDays)
  const oldPortion = Math.round((oldFee / 30) * remainingDays)
  return { amount: newPortion - oldPortion, newPortion, oldPortion, remainingDays }
}
function sleep(ms: number) { return new Promise<void>((r) => setTimeout(r, ms)) }

const LEVEL_ONBOARDING: Record<UserLevel, string[]> = {
  FREE:         ['기본 변환 (5 GiB/월)', '기본 저장 (5 GiB)', '스트리밍 (15 GiB/월)'],
  STANDARD:     ['변환 50 GiB/월', '저장 100 GiB', '스트리밍 200 GiB/월', '데이터 다운로드 (종량제)'],
  PROFESSIONAL: ['변환 200 GiB/월', '저장 500 GiB', '스트리밍 1,000 GiB/월', '데이터 다운로드 (종량제)'],
}

// ── Step 3: Pre-checkout 명세 모달 ───────────────────────

function CheckoutModal({
  from, to, nextBillingDate, hasPaymentMethod, onConfirm, onCancel,
}: {
  from: UserLevel
  to: UserLevel
  nextBillingDate?: string
  hasPaymentMethod: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const fromMeta = LEVEL_META[from]
  const toMeta   = LEVEL_META[to]
  const fromFee  = LEVEL_QUOTA[from].monthlyFeeNum
  const toFee    = LEVEL_QUOTA[to].monthlyFeeNum
  const isUpgrade = toFee > fromFee
  const blocked   = isUpgrade && !hasPaymentMethod
  const { amount: proratedAmt, newPortion, oldPortion, remainingDays } = useMemo(
    () => calcProration(toFee, fromFee), [toFee, fromFee]
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md mx-4 shadow-2xl">

        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-base font-semibold text-white">요금제 변경 확인</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-5">

          {/* 플랜 변경 방향 */}
          <div className="flex items-center justify-center gap-4">
            <span className={`text-sm font-semibold px-3 py-1.5 rounded-full ${fromMeta.bg} ${fromMeta.color}`}>
              {fromMeta.label}
            </span>
            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            <span className={`text-sm font-semibold px-3 py-1.5 rounded-full ${toMeta.bg} ${toMeta.color}`}>
              {toMeta.label}
            </span>
          </div>

          {/* 영수증 명세 */}
          <div className="bg-slate-900/60 rounded-xl p-4 space-y-3 border border-slate-700/50">
            {isUpgrade ? (
              <>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">오늘 결제 명세</p>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">추가 결제 금액</span>
                  <span className="text-white font-semibold">{formatKRW(proratedAmt)}</span>
                </div>
                <div className="text-[11px] text-slate-500 space-y-0.5">
                  <p>(새 등급 {toFee.toLocaleString('ko-KR')}원 ÷ 30일 × 잔여 {remainingDays}일)
                    = {newPortion.toLocaleString('ko-KR')}원</p>
                  <p>− (기존 등급 {fromFee.toLocaleString('ko-KR')}원 ÷ 30일 × 잔여 {remainingDays}일)
                    = {oldPortion.toLocaleString('ko-KR')}원</p>
                </div>
                <div className="border-t border-slate-700 pt-3 flex justify-between text-sm">
                  <span className="text-slate-400">다음 정기 결제 ({nextBillingDate ?? '-'})</span>
                  <span className="text-white font-semibold">{formatFee(toFee)}</span>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">다운그레이드 안내</p>
                <div className="rounded-lg bg-amber-900/30 border border-amber-700/50 px-3 py-2.5">
                  <p className="text-amber-300 text-sm font-medium">즉시 적용되지 않습니다</p>
                  <p className="text-amber-200/70 text-xs mt-0.5">
                    현재 결제 주기 종료 후({nextBillingDate ?? '다음 결제일'}) 변경됩니다.
                    그 전까지 현재 {fromMeta.label} 혜택이 유지됩니다.
                  </p>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">오늘 청구 금액</span>
                  <span className="text-emerald-400 font-semibold">없음</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">{nextBillingDate ?? '다음 결제일'}부터</span>
                  <span className="text-white font-semibold">{formatFee(toFee)}</span>
                </div>
              </>
            )}
          </div>

          {/* 결제 수단 (업그레이드 시만 표시) */}
          {isUpgrade && (
            <div className={`rounded-xl p-4 border ${hasPaymentMethod ? 'bg-slate-900/60 border-slate-700/50' : 'bg-red-900/20 border-red-700/60'}`}>
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-2">결제 수단</p>
              {hasPaymentMethod ? (
                <p className="text-sm text-slate-300">등록된 결제 수단으로 청구됩니다.</p>
              ) : (
                <>
                  <p className="text-sm text-red-400 font-semibold">등록된 결제 수단이 없습니다</p>
                  <p className="text-[11px] text-red-400/70 mt-1">업그레이드를 진행하려면 결제 수단을 먼저 등록해 주세요.</p>
                </>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              onClick={onCancel}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-slate-400 border border-slate-600 hover:border-slate-400 hover:text-white transition-colors"
            >
              취소
            </button>
            <button
              onClick={onConfirm}
              disabled={blocked}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                blocked
                  ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                  : isUpgrade
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : 'bg-amber-700 hover:bg-amber-600 text-white'
              }`}
            >
              {blocked ? '결제 수단 미등록' : isUpgrade ? '결제 진행 →' : '다운그레이드 예약'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Steps 4-5: 처리 중 오버레이 ─────────────────────────

function ProcessingOverlay() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="text-center space-y-4">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-white font-medium">처리하고 있습니다</p>
        <p className="text-slate-400 text-sm">창을 닫거나 새로고침하지 마세요.</p>
      </div>
    </div>
  )
}

// ── Step 6: 성공 / 예약 완료 피드백 ──────────────────────

function ResultModal({
  newLevel, isScheduled, nextBillingDate, currentLevel, onClose,
}: {
  newLevel: UserLevel
  isScheduled: boolean
  nextBillingDate?: string
  currentLevel: UserLevel
  onClose: () => void
}) {
  const meta    = LEVEL_META[newLevel]
  const curMeta = LEVEL_META[currentLevel]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
        <div className={`h-2 w-full ${isScheduled ? 'bg-amber-600' : meta.bg}`} />

        <div className="p-8 text-center space-y-4">
          {isScheduled ? (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-900/40 border-2 border-amber-600 flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-bold text-lg">다운그레이드 예약 완료</p>
                <p className="text-slate-400 text-sm mt-1">
                  <span className="text-amber-300 font-medium">{nextBillingDate ?? '다음 결제일'}</span>에{' '}
                  <span className={`font-medium ${meta.color}`}>{meta.label}</span> 요금제로 변경됩니다.
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 text-left space-y-1.5">
                <p className="text-xs text-slate-500 mb-2">그 전까지 유지되는 혜택 ({curMeta.label})</p>
                {LEVEL_ONBOARDING[currentLevel].map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm">
                    <span className={`w-1.5 h-1.5 rounded-full ${curMeta.bg} flex-shrink-0`} />
                    <span className="text-slate-300">{f}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">예약 취소는 요금제 페이지의 [예약 취소] 버튼을 누르세요.</p>
            </>
          ) : (
            <>
              <div className={`w-16 h-16 rounded-full ${meta.accentBg} border-2 ${meta.border} flex items-center justify-center mx-auto`}>
                <svg className={`w-8 h-8 ${meta.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"
                    style={{ strokeDasharray: 30, strokeDashoffset: 0, animation: 'drawCheck 0.4s ease forwards' }} />
                </svg>
              </div>
              <div>
                <p className="text-white font-bold text-lg">
                  <span className={meta.color}>{meta.label}</span> 요금제로 변경되었습니다!
                </p>
                <p className="text-slate-400 text-sm mt-1">이제 다음 기능을 이용하실 수 있습니다.</p>
              </div>
              <ul className="text-left space-y-1.5 bg-slate-900/50 rounded-xl p-4">
                {LEVEL_ONBOARDING[newLevel].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm">
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.bg} flex-shrink-0`} />
                    <span className="text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <button
            onClick={onClose}
            className={`w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-colors ${
              isScheduled ? 'bg-amber-700 hover:bg-amber-600' : `${meta.bg} hover:opacity-90`
            }`}
          >
            확인
          </button>
        </div>
      </div>
    </div>
  )
}

// ── 사용량 쿼터 바 ───────────────────────────────────────

function QuotaBar({ item, label, note }: { item: QuotaItem; label: string; note?: string }) {
  const barColor = item.exceeded
    ? 'bg-red-500'
    : item.percentage >= 90
      ? 'bg-red-400'
      : item.percentage >= 75
        ? 'bg-amber-400'
        : 'bg-emerald-500'

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-300 font-medium">{label}</span>
        <span className={`font-semibold tabular-nums ${item.exceeded ? 'text-red-400' : 'text-slate-200'}`}>
          {item.usedGib.toFixed(2)} / {item.limitGib} GiB
          {item.exceeded && <span className="ml-1.5 text-xs text-red-400 font-normal">한도 초과</span>}
        </span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${item.percentage}%` }}
        />
      </div>
      {note && <p className="text-[11px] text-slate-500">{note}</p>}
    </div>
  )
}

// ── 청구 내역 상태 뱃지 ──────────────────────────────────

function StatusBadge({ status }: { status: Invoice['status'] }) {
  const map = {
    FREE:    { label: '-',       cls: 'text-slate-400' },
    PAID:    { label: '납부완료', cls: 'text-emerald-400' },
    PENDING: { label: '청구예정', cls: 'text-yellow-400' },
  }
  const { label, cls } = map[status]
  return <span className={`text-xs font-medium ${cls}`}>{label}</span>
}

// ── 메인 컴포넌트 ────────────────────────────────────────

export default function PlanPage() {
  const navigate       = useNavigate()
  const { updateUser } = useAuth()

  const [summary,        setSummary]        = useState<BillingSummary | null>(null)
  const [invoices,       setInvoices]       = useState<Invoice[]>([])
  const [quota,          setQuota]          = useState<UsageQuota | null>(null)
  const [loading,        setLoading]        = useState(true)
  const [checkoutTarget, setCheckoutTarget] = useState<UserLevel | null>(null)
  const [processing,     setProcessing]     = useState(false)
  const [result,         setResult]         = useState<{ level: UserLevel; scheduled: boolean } | null>(null)
  const [changeError,    setChangeError]    = useState<string | null>(null)
  const [cancelling,     setCancelling]     = useState(false)

  useEffect(() => {
    Promise.allSettled([billingApi.getSummary(), billingApi.getInvoices(), billingApi.getQuota()]).then(([s, i, q]) => {
      if (s.status === 'fulfilled') setSummary(s.value)
      if (i.status === 'fulfilled') setInvoices(i.value)
      if (q.status === 'fulfilled') setQuota(q.value)
      setLoading(false)
    })
  }, [])

  const currentLevel   = summary?.userLevel as UserLevel | undefined
  const currentFee     = currentLevel ? LEVEL_QUOTA[currentLevel].monthlyFeeNum : 0
  const scheduledLevel = summary?.scheduledLevel as UserLevel | null | undefined

  const refreshBilling = async () => {
    const [s, i, q] = await Promise.allSettled([billingApi.getSummary(), billingApi.getInvoices(), billingApi.getQuota()])
    if (s.status === 'fulfilled') setSummary(s.value)
    if (i.status === 'fulfilled') setInvoices(i.value)
    if (q.status === 'fulfilled') setQuota(q.value)
  }

  const handleConfirmChange = async () => {
    if (!checkoutTarget || !currentLevel) return
    const target      = checkoutTarget
    const targetFee   = LEVEL_QUOTA[target].monthlyFeeNum
    const isDowngrade = targetFee < currentFee

    setCheckoutTarget(null)
    setProcessing(true)
    setChangeError(null)
    try {
      await sleep(1200)
      const updated = await userApi.updateLevel(target)
      if (!isDowngrade) {
        updateUser({ userLevel: updated.userLevel as UserLevel })
      }
      await refreshBilling()
      setResult({ level: target, scheduled: isDowngrade })
    } catch {
      setChangeError('요금제 변경에 실패했습니다. 다시 시도해주세요.')
    } finally {
      setProcessing(false)
    }
  }

  const handleCancelScheduled = async () => {
    setCancelling(true)
    setChangeError(null)
    try {
      await userApi.cancelScheduledLevel()
      await refreshBilling()
    } catch {
      setChangeError('예약 취소에 실패했습니다. 다시 시도해주세요.')
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">

      {/* 헤더 */}
      <header className="sticky top-0 z-10 bg-slate-800 border-b border-slate-700 px-6 py-3 flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          대시보드
        </button>
        <h1 className="text-base font-semibold text-white">요금제 · 결제</h1>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-10">

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500 text-sm">불러오는 중...</div>
        ) : (
          <>
            {changeError && (
              <div className="bg-red-900/30 border border-red-700 text-red-300 text-sm rounded-xl px-4 py-3">
                {changeError}
              </div>
            )}

            {/* 다운그레이드 예약 안내 배너 */}
            {scheduledLevel && currentLevel && (
              <div className="flex items-center justify-between gap-4 bg-amber-900/30 border border-amber-700/60 rounded-2xl px-5 py-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-amber-300 font-semibold text-sm">다운그레이드 예약됨</p>
                    <p className="text-amber-200/70 text-xs mt-0.5">
                      <span className="font-medium">{summary?.nextBillingDate}</span>에{' '}
                      <span className={`font-medium ${LEVEL_META[scheduledLevel].color}`}>
                        {LEVEL_META[scheduledLevel].label}
                      </span>{' '}
                      요금제로 변경됩니다. 그 전까지 현재 {LEVEL_META[currentLevel].label} 혜택이 유지됩니다.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCancelScheduled}
                  disabled={cancelling}
                  className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg border border-amber-600 text-amber-300 hover:bg-amber-800/40 transition-colors disabled:opacity-50"
                >
                  {cancelling ? '취소 중...' : '예약 취소'}
                </button>
              </div>
            )}

            {/* ── Step 1: 현재 요금제 ── */}
            {summary && currentLevel && (() => {
              const meta  = LEVEL_META[currentLevel]
              const quota = LEVEL_QUOTA[currentLevel]
              return (
                <section>
                  <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">현재 요금제</h2>
                  <div className={`rounded-2xl border ${meta.border} ${meta.accentBg} p-6`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${meta.bg} ${meta.color}`}>
                          {meta.label}
                        </span>
                        <div>
                          <p className="text-white font-semibold text-lg leading-tight">{meta.label}</p>
                          <p className="text-slate-400 text-sm mt-0.5">{quota.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-white">{formatFee(summary.monthlyFee)}</p>
                        {summary.monthlyFee > 0 && (
                          <p className="text-slate-400 text-xs mt-1">
                            다음 결제일{' '}
                            <span className="text-white font-medium">{summary.nextBillingDate}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              )
            })()}

            {/* ── 이번 달 사용량 ── */}
            {quota && (
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    이번 달 사용량
                  </h2>
                  <span className="text-[11px] text-slate-500">{quota.billingCycle}</span>
                </div>
                <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6 space-y-5">
                  <QuotaBar
                    item={quota.conversion}
                    label="변환"
                    note="이번 달 업로드한 파일 크기 합계 / 월 변환 한도"
                  />
                  <QuotaBar
                    item={quota.storage}
                    label="저장"
                    note="현재 저장된 타일 데이터 총 용량"
                  />
                  <QuotaBar
                    item={quota.streaming}
                    label="스트리밍"
                    note="이번 달 3D 뷰어를 통해 전송된 타일 데이터"
                  />
                  <div className="flex items-center justify-between text-sm pt-1 border-t border-slate-700/50">
                    <span className="text-slate-300 font-medium">다운로드</span>
                    <span className={`font-semibold ${quota.downloadPolicy === '미제공' ? 'text-slate-500' : 'text-slate-200'}`}>
                      {quota.downloadPolicy}
                    </span>
                  </div>
                </div>
              </section>
            )}

            {/* ── Step 2: 플랜 비교 및 선택 ── */}
            <section>
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">요금제 변경</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {LEVEL_ORDER.map((level) => {
                  const meta      = LEVEL_META[level]
                  const quota     = LEVEL_QUOTA[level]
                  const isActive  = level === currentLevel
                  const isPending = level === scheduledLevel
                  const levelFee  = quota.monthlyFeeNum
                  const isUpgrade = levelFee > currentFee

                  return (
                    <div
                      key={level}
                      className={`rounded-2xl border p-6 flex flex-col transition-all duration-200 ${
                        isActive
                          ? `${meta.border} ${meta.accentBg}`
                          : isPending
                            ? 'border-amber-700/60 bg-amber-900/10'
                            : 'border-slate-700 bg-slate-800 hover:border-slate-500'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${meta.bg} ${meta.color}`}>
                          {meta.label}
                        </span>
                        {isActive && (
                          <span className="text-[10px] font-semibold text-slate-400 border border-slate-600 rounded-full px-2 py-0.5">
                            현재
                          </span>
                        )}
                        {isPending && (
                          <span className="text-[10px] font-semibold text-amber-400 border border-amber-700 rounded-full px-2 py-0.5">
                            변경 예정
                          </span>
                        )}
                        {!isActive && !isPending && isUpgrade && (
                          <span className="text-[10px] font-semibold text-blue-400">▲ 업그레이드</span>
                        )}
                        {!isActive && !isPending && !isUpgrade && (
                          <span className="text-[10px] font-semibold text-slate-500">▼ 다운그레이드</span>
                        )}
                      </div>

                      <p className="text-2xl font-bold text-white mb-5">{formatFee(levelFee)}</p>

                      <ul className="space-y-2.5 flex-1 mb-6">
                        {[
                          { label: '변환',     value: `${quota.conversionGib} GiB/월` },
                          { label: '저장',     value: `${quota.storageGib} GiB` },
                          { label: '스트리밍', value: `${quota.streamingGib} GiB/월` },
                          { label: '다운로드', value: quota.download },
                        ].map(({ label, value }) => (
                          <li key={label} className="flex justify-between text-sm">
                            <span className="text-slate-500">{label}</span>
                            <span className="text-slate-300 font-medium">{value}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA — 상태에 따라 분기 */}
                      {isActive ? (
                        <button disabled className={`w-full py-2.5 rounded-xl text-sm font-semibold ${meta.bg} ${meta.color} opacity-50 cursor-default`}>
                          현재 요금제
                        </button>
                      ) : isPending ? (
                        <button
                          onClick={handleCancelScheduled}
                          disabled={cancelling}
                          className="w-full py-2.5 rounded-xl text-sm font-medium border border-amber-600 text-amber-300 hover:bg-amber-800/30 transition-colors disabled:opacity-50"
                        >
                          {cancelling ? '취소 중...' : '예약 취소'}
                        </button>
                      ) : isUpgrade ? (
                        <button
                          onClick={() => setCheckoutTarget(level)}
                          className="w-full py-2.5 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/30 transition-colors"
                        >
                          업그레이드 →
                        </button>
                      ) : (
                        <button
                          onClick={() => setCheckoutTarget(level)}
                          className="w-full py-2.5 rounded-xl text-sm font-medium border border-slate-600 text-slate-400 hover:border-slate-400 hover:text-white transition-colors"
                        >
                          다운그레이드
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
              <p className="text-xs text-slate-600 mt-3 text-center">
                다운그레이드는 현재 결제 주기 종료 후 자동 적용됩니다. 취소 시 현재 등급이 유지됩니다.
              </p>
            </section>

            {/* ── 결제 정보 ── */}
            <section>
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">결제 정보</h2>
              <div className="space-y-4">

                <div className="bg-slate-800 rounded-2xl border border-slate-700 p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-300 mb-1">결제 수단</h3>
                      <p className="text-slate-500 text-sm">등록된 결제 수단이 없습니다.</p>
                    </div>
                    <button
                      disabled
                      className="text-xs font-medium px-4 py-2 rounded-lg bg-slate-700 text-slate-500 border border-slate-600 cursor-not-allowed"
                      title="준비 중인 기능입니다."
                    >
                      카드 등록 (준비 중)
                    </button>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-2xl border border-slate-700 p-5">
                  <h3 className="text-sm font-semibold text-slate-300 mb-4">청구 내역</h3>
                  {invoices.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-6">청구 내역이 없습니다.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[11px] text-slate-500 border-b border-slate-700">
                            <th className="text-left pb-2 font-medium">청구월</th>
                            <th className="text-left pb-2 font-medium">요금제</th>
                            <th className="text-right pb-2 font-medium">기본료</th>
                            <th className="text-right pb-2 font-medium">상태</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                          {invoices.map((inv) => (
                            <tr key={inv.billingCycle}>
                              <td className="py-2.5 text-slate-300">{inv.billingCycle}</td>
                              <td className="py-2.5">
                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${LEVEL_META[inv.userLevel as UserLevel]?.bg} ${LEVEL_META[inv.userLevel as UserLevel]?.color}`}>
                                  {inv.levelLabel}
                                </span>
                              </td>
                              <td className="py-2.5 text-right text-slate-300">
                                {inv.basicFee === 0 ? '-' : inv.basicFee.toLocaleString('ko-KR') + '원'}
                              </td>
                              <td className="py-2.5 text-right">
                                <StatusBadge status={inv.status} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      {/* Step 3: Pre-checkout 모달 */}
      {checkoutTarget && currentLevel && (
        <CheckoutModal
          from={currentLevel}
          to={checkoutTarget}
          nextBillingDate={summary?.nextBillingDate}
          hasPaymentMethod={summary?.hasPaymentMethod ?? false}
          onConfirm={handleConfirmChange}
          onCancel={() => setCheckoutTarget(null)}
        />
      )}

      {/* Steps 4-5: 처리 중 */}
      {processing && <ProcessingOverlay />}

      {/* Step 6: 결과 피드백 */}
      {result && currentLevel && (
        <ResultModal
          newLevel={result.level}
          isScheduled={result.scheduled}
          nextBillingDate={summary?.nextBillingDate}
          currentLevel={currentLevel}
          onClose={() => setResult(null)}
        />
      )}
    </div>
  )
}
